/**
 * @file MapOfAgExportModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-09-18
 * @description How a Map of Ag report should go out: as a workbook or a PDF,
 *              downloaded or emailed.
 *
 *              The format used to be a dropdown sitting in the filter row,
 *              which made it a filter — something you set once and forget —
 *              when it is really part of the act of exporting. Worse, it left
 *              the Preview button disabled whenever the dropdown said Excel,
 *              for a reason nobody could see from looking at it.
 *
 *              Preview is not in here at all. It is always a PDF, always
 *              available, and one click — asking someone to open a dialog to
 *              look at their own report would be the same mistake again.
 */

import { useState, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import './MapOfAgExportModal.css';

function MapOfAgExportModal({
  isOpen, title = 'Export the report', busy = false,
  defaultEmail = '', defaultFormat = 'xlsx', onExport, onClose
}) {
  const [format, setFormat] = useState(defaultFormat);
  const [byEmail, setByEmail] = useState(false);
  const [to, setTo] = useState(defaultEmail);

  useEffect(() => {
    if (!isOpen) return;
    setFormat(defaultFormat);
    setByEmail(false);
    setTo(defaultEmail);
  }, [isOpen, defaultEmail, defaultFormat]);

  useSlideOutButton([isOpen, format, byEmail, busy]);

  if (!isOpen) return null;

  const go = () => onExport({
    format,
    mode: byEmail ? 'email' : 'download',
    to: byEmail ? to.trim() : undefined
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="map-of-ag-export"
      size="sm"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              onClick={go}
              disabled={busy || (byEmail && !to.trim())}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">{byEmail ? 'send' : 'download'}</span>
              </div>
              <span className="btn-label">
                {busy ? 'Working…' : byEmail ? 'Send' : 'Download'}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <div className="moae-formats">
        {[
          { value: 'xlsx', icon: 'table_view', label: 'Excel workbook', note: 'A sheet per customer, with the reference sheets.' },
          { value: 'pdf', icon: 'picture_as_pdf', label: 'PDF', note: 'A page per customer, ready to send on.' }
        ].map((f) => (
          <button
            key={f.value}
            type="button"
            className={`moae-format${format === f.value ? ' is-picked' : ''}`}
            onClick={() => setFormat(f.value)}
          >
            <span className="material-symbols-rounded">{f.icon}</span>
            <span className="moae-format-label">{f.label}</span>
            <span className="moae-format-note">{f.note}</span>
          </button>
        ))}
      </div>

      <div className="moae-email" style={{ fontSize: 'small' }}>
        <TickCheckbox
          id="moae-by-email"
          checked={byEmail}
          onChange={(e) => setByEmail(e.target.checked)}
          label="Email it instead of downloading"
        />
        {byEmail && (
          <div className="moae-email-fields">
            <label htmlFor="moae-to">To</label>
            <Input
              id="moae-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="name@example.com"
            />
            <p className="moae-note">Sent from admin@excel-agri.com. Defaults to your own address.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default MapOfAgExportModal;
